import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Search,
  Send,
  ShieldAlert,
  UserRound,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { MIN_TEXT_WORDS, getMinimumWordsError } from "../../utils/textValidation";

const statusStyle = {
  Open: "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900",
  Reviewing: "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900",
  Resolved: "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900",
  Dismissed: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

const displayType = (type) => type === "Complaint" ? "Grievance" : (type || "Grievance");

const getComplaintCacheKey = (employee) => {
  const id = employee?._id || employee?.id || "self";
  return `employee_complaints_${id}`;
};

const readComplaintCache = (employee) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(getComplaintCacheKey(employee)) || "{}");
    return Array.isArray(parsed.data) ? parsed.data : [];
  } catch {
    return [];
  }
};

const writeComplaintCache = (employee, complaints) => {
  try {
    localStorage.setItem(getComplaintCacheKey(employee), JSON.stringify({
      data: complaints,
      savedAt: Date.now(),
    }));
  } catch {
    // The live API still works if local cache is unavailable.
  }
};

const getDirectoryCacheKey = (employee) => {
  const id = employee?._id || employee?.id || "self";
  return `employee_complaint_directory_${id}`;
};

const readDirectoryCache = (employee) => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(getDirectoryCacheKey(employee)) || "{}");
    return Array.isArray(parsed.data) ? parsed.data : [];
  } catch {
    return [];
  }
};

const writeDirectoryCache = (employee, directory) => {
  try {
    sessionStorage.setItem(getDirectoryCacheKey(employee), JSON.stringify({
      data: directory,
      savedAt: Date.now(),
    }));
  } catch {
    // Suggestions can still load from API if session storage is unavailable.
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const EmployeeComplaints = () => {
  const { employee } = useAuth();
  const location = useLocation();
  const canMonitorComplaints = employee?.role === "lead"
    || String(employee?.role || "").toLowerCase() === "hr"
    || String(employee?.team || "").toLowerCase() === "hr";
  const cachedComplaints = useMemo(() => readComplaintCache(employee), [employee]);
  const cachedDirectory = useMemo(() => readDirectoryCache(employee), [employee]);
  const [complaints, setComplaints] = useState(cachedComplaints);
  const [teamComplaints, setTeamComplaints] = useState([]);
  const [employeeDirectory, setEmployeeDirectory] = useState(cachedDirectory);
  const [employeeId, setEmployeeId] = useState("");
  const [results, setResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [entryType, setEntryType] = useState("Grievance");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [indicator, setIndicator] = useState("");

  const fetchComplaints = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/complaints", { params: { limit: 80 } });
      const next = Array.isArray(res.data) ? res.data : [];
      setComplaints(next);
      writeComplaintCache(employee, next);
    } catch (error) {
      console.error("Failed to load complaints:", error);
    }
  }, [employee]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const fetchTeamComplaints = useCallback(async () => {
    if (!canMonitorComplaints) return;
    try {
      const res = await api.get("/api/employee/team/complaints", { params: { limit: 120 }, cache: false });
      setTeamComplaints(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load team complaints:", error);
    }
  }, [canMonitorComplaints]);

  useEffect(() => {
    fetchTeamComplaints();
  }, [fetchTeamComplaints]);

  useEffect(() => {
    if (!canMonitorComplaints || !location.search.includes("view=employee-list")) return;
    const timer = window.setTimeout(() => {
      document.getElementById("employee-complaints-list")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [canMonitorComplaints, location.search]);

  const fetchEmployeeDirectory = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/chat/directory", { cache: false });
      const next = (Array.isArray(res.data) ? res.data : [])
        .filter((item) => item.userModel === "Employee" && item._id !== employee?._id);
      setEmployeeDirectory(next);
      writeDirectoryCache(employee, next);
    } catch (error) {
      console.error("Failed to load employee suggestions:", error);
    }
  }, [employee]);

  useEffect(() => {
    fetchEmployeeDirectory();
  }, [fetchEmployeeDirectory]);

  const findLocalMatches = useCallback((value) => {
    const query = String(value || "").trim().toLowerCase();
    if (!query) return [];

    return employeeDirectory
      .map((item) => {
        const id = String(item.employeeId || "").toLowerCase();
        const name = String(item.name || "").toLowerCase();
        const email = String(item.email || "").toLowerCase();
        const startsWithId = id.startsWith(query);
        const startsWithName = name.startsWith(query);
        const includesMatch = id.includes(query) || name.includes(query) || email.includes(query);
        if (!startsWithId && !startsWithName && !includesMatch) return null;
        return {
          item,
          score: startsWithId ? 0 : startsWithName ? 1 : 2,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score || String(a.item.employeeId || "").localeCompare(String(b.item.employeeId || "")))
      .slice(0, 8)
      .map((entry) => entry.item);
  }, [employeeDirectory]);

  const chooseEmployee = useCallback((item) => {
    if (!item) return;
    setSelectedEmployee(item);
    setEmployeeId(item.employeeId || "");
    setResults([]);
    setSuggestionsOpen(false);
    setIndicator(`Selected ${item.name || item.employeeId}`);
    window.setTimeout(() => setIndicator(""), 1400);
  }, []);

  const handleSearch = useCallback(async (valueOverride, options = {}) => {
    const value = String(valueOverride ?? employeeId).trim();
    if (!value) return;

    const localMatches = findLocalMatches(value);
    if (localMatches.length) {
      setResults(localMatches);
      const exactMatch = localMatches.find((item) => item.employeeId?.toLowerCase() === value.toLowerCase());
      if (exactMatch || options.selectFirst) {
        chooseEmployee(exactMatch || localMatches[0]);
      } else {
        setSuggestionsOpen(true);
      }
      return;
    }

    setSearching(true);
    setSelectedEmployee(null);
    try {
      const res = await api.get("/api/employee/complaints/employees", {
        params: { query: value },
        cache: false,
      });
      const next = Array.isArray(res.data) ? res.data : [];
      setResults(next);
      const exactMatch = next.find((item) => item.employeeId?.toLowerCase() === value.toLowerCase());
      if (exactMatch || (options.selectFirst && next.length)) {
        chooseEmployee(exactMatch || next[0]);
      } else {
        setSuggestionsOpen(next.length > 0);
      }
      if (!next.length && !options.silent) setIndicator("No employee found for this ID.");
    } catch (error) {
      if (!options.silent) setIndicator(error.response?.data?.message || "Could not search employee.");
    } finally {
      setSearching(false);
      if (!options.silent) window.setTimeout(() => setIndicator(""), 2200);
    }
  }, [chooseEmployee, employeeId, findLocalMatches]);

  useEffect(() => {
    const value = employeeId.trim();
    if (selectedEmployee && selectedEmployee.employeeId === value) {
      setResults([]);
      setSuggestionsOpen(false);
      return undefined;
    }
    if (value.length < 1) {
      setResults([]);
      setSelectedEmployee(null);
      setSuggestionsOpen(false);
      return undefined;
    }

    const next = findLocalMatches(value);
    setResults(next);
    setSuggestionsOpen(next.length > 0);
    return undefined;
  }, [employeeId, findLocalMatches, selectedEmployee]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const needsEmployee = entryType === "Grievance";
    const targetEmployee = needsEmployee ? selectedEmployee : null;
    const targetId = targetEmployee?._id;
    const details = description.trim();
    if ((needsEmployee && !targetId) || !details) return;

    const detailsError = getMinimumWordsError(details, entryType);
    if (detailsError) {
      setIndicator(detailsError);
      return;
    }

    const optimisticId = `grievance-${Date.now()}`;
    const optimisticComplaint = {
      _id: optimisticId,
      raisedBy: employee,
      againstEmployee: targetEmployee,
      type: entryType,
      subject: subject.trim(),
      description: details,
      status: "Open",
      createdAt: new Date().toISOString(),
    };

    const previous = complaints;
    const next = [optimisticComplaint, ...complaints].slice(0, 80);
    setComplaints(next);
    writeComplaintCache(employee, next);
    setSubmitting(true);
    setIndicator(`Submitting ${entryType.toLowerCase()}...`);

    try {
      const res = await api.post("/api/employee/complaints", {
        againstEmployeeId: targetId,
        employeeId: employeeId.trim(),
        type: entryType,
        subject,
        description,
      }, { skipSaveToast: true });

      setComplaints((current) => {
        const updated = current.map((item) => item._id === optimisticId ? res.data : item);
        writeComplaintCache(employee, updated);
        return updated;
      });
      setSubject("");
      setDescription("");
      setEntryType("Grievance");
      setEmployeeId("");
      setResults([]);
      setSelectedEmployee(null);
      fetchTeamComplaints();
      setIndicator(`${entryType} submitted to admin.`);
    } catch (error) {
      setComplaints(previous);
      writeComplaintCache(employee, previous);
      setIndicator(error.response?.data?.message || `${entryType} submission failed.`);
    } finally {
      setSubmitting(false);
      window.setTimeout(() => setIndicator(""), 2600);
    }
  };

  return (
    <div className="grievance-premium-wrapper text-[var(--text-primary)]">
      <main className="max-w-[1320px] mx-auto px-4">
        <section className="grievance-premium-hero">
          <div className="grievance-hero-content">
            <span className="premium-eyebrow text-[#e11d48]">Employee Grievance & Request</span>
            <h1>Raise grievance or request</h1>
            <p className="mt-2 text-[var(--text-secondary)] font-medium">
              Enter employee ID, mention the employee, and send it directly to admin.
            </p>
          </div>

          {indicator && (
            <div className="rounded-xl border border-[#e11d48]/20 bg-[#e11d48]/10 px-5 py-3 text-sm font-bold text-[#e11d48] dark:border-[#e11d48]/30 dark:bg-[#e11d48]/20 dark:text-rose-200 shadow-sm animate-pulse">
              {indicator}
            </div>
          )}
        </section>

        <div className="grievance-premium-layout">
          <form onSubmit={handleSubmit} className="grievance-premium-board">
            <div className="grievance-board-header">
              <h2>Details</h2>
            </div>

            <div className="grievance-input-wrap !mt-0">
              <label>Type</label>
              <div className="flex gap-2 bg-[var(--bg-primary)] p-1.5 rounded-2xl border border-[var(--border-color)]">
                {["Grievance", "Request"].map((item) => (
                  <button
                    type="button"
                    key={item}
                    onClick={() => {
                      setEntryType(item);
                      if (item === "Request") {
                        setEmployeeId("");
                        setResults([]);
                        setSelectedEmployee(null);
                        setSuggestionsOpen(false);
                      }
                    }}
                    className={`flex-1 grievance-type-btn ${entryType === item ? "active" : ""}`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {entryType === "Grievance" && (
              <div className="grievance-input-wrap">
                <label>Employee ID</label>
                <div className="flex gap-2">
                  <input
                    value={employeeId}
                    onChange={(event) => {
                      setEmployeeId(event.target.value);
                      setSelectedEmployee(null);
                      setSuggestionsOpen(Boolean(event.target.value.trim()));
                    }}
                    onFocus={() => {
                      if (results.length && !selectedEmployee) setSuggestionsOpen(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSearch(undefined, { selectFirst: true });
                      }
                    }}
                    placeholder="Type employee ID or name"
                    className="grievance-input flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => handleSearch(undefined, { selectFirst: true })}
                    disabled={searching || selectedEmployee || !employeeId.trim()}
                    className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-black text-white transition disabled:opacity-50 ${
                      selectedEmployee
                        ? "bg-emerald-600 hover:bg-emerald-700 shadow-md shadow-emerald-600/20"
                        : results.length
                          ? "bg-[#e11d48] hover:bg-[#be123c] shadow-md shadow-[#e11d48]/20"
                          : "bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                    }`}
                  >
                    {selectedEmployee ? <CheckCircle2 size={16} /> : <Search size={16} />}
                    {searching ? "Checking" : selectedEmployee ? "Selected" : results.length ? "Select" : "Find"}
                  </button>
                </div>
              </div>
            )}

            {entryType === "Grievance" && selectedEmployee && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
                      Selected employee
                    </p>
                    <p className="mt-1 truncate text-base font-black text-slate-950 dark:text-white">
                      {selectedEmployee.name}
                    </p>
                    <p className="truncate text-sm font-bold text-slate-600 dark:text-slate-300">
                      ID: {selectedEmployee.employeeId} - {selectedEmployee.team || "Team not set"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEmployee(null);
                      setEmployeeId("");
                      setResults([]);
                      setSuggestionsOpen(false);
                    }}
                    className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-black text-emerald-700 hover:bg-emerald-100 transition-colors dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 dark:hover:bg-emerald-900"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}

            {entryType === "Grievance" && (suggestionsOpen || searching) && !selectedEmployee && (
              <div className="mt-3 max-h-64 overflow-y-auto rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-2 shadow-lg">
                {searching && (
                  <div className="px-3 py-2 text-xs font-bold text-[var(--text-secondary)]">
                    Searching employee IDs...
                  </div>
                )}
                {results.map((item) => {
                  const active = selectedEmployee?._id === item._id;
                  return (
                    <button
                      type="button"
                      key={item._id}
                      onClick={() => chooseEmployee(item)}
                      className={`w-full rounded-xl border px-4 py-3 text-left transition mb-1 ${
                        active
                          ? "border-[#e11d48]/30 bg-[#e11d48]/5 dark:border-[#e11d48]/50 dark:bg-[#e11d48]/10"
                          : "border-transparent hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black">{item.name}</p>
                          <p className="truncate text-xs font-semibold text-[var(--text-secondary)]">
                            ID: {item.employeeId} - {item.team || "Team not set"}
                          </p>
                        </div>
                        {active && <CheckCircle2 size={18} className="text-[#e11d48]" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="grievance-input-wrap">
              <label>Subject</label>
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={140}
                placeholder={`Short ${entryType.toLowerCase()} title`}
                className="grievance-input"
              />
            </div>

            <div className="grievance-input-wrap">
              <label>{entryType}</label>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={5}
                maxLength={5000}
                placeholder={entryType === "Request" ? `Explain your request clearly in at least ${MIN_TEXT_WORDS} words...` : `Explain what happened clearly in at least ${MIN_TEXT_WORDS} words...`}
                className="grievance-input resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !description.trim() || (entryType === "Grievance" && !(selectedEmployee?._id))}
              className="grievance-submit-btn"
            >
              <Send size={18} />
              {submitting ? "Submitting..." : `Submit ${entryType.toLowerCase()}`}
            </button>
          </form>

          <div className="flex flex-col gap-6">
            <section className="grievance-premium-board !p-6">
              <div className="grievance-board-header !mb-6">
                <div>
                  <h2 className="text-xl font-bold">My history</h2>
                  <p className="text-sm text-[var(--text-secondary)]">{complaints.length} submitted</p>
                </div>
                <Clock3 size={20} className="text-[#e11d48]" />
              </div>

              {!complaints.length ? (
                <div className="rounded-2xl border border-dashed border-[var(--border-color)] p-8 text-center bg-[var(--bg-primary)]/50">
                  <AlertCircle className="mx-auto mb-3 text-[var(--text-secondary)]" size={24} />
                  <p className="text-sm font-bold text-[var(--text-secondary)]">No grievances or requests submitted yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {complaints.map((complaint) => (
                    <article key={complaint._id} className="grievance-history-item">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] uppercase">
                            <span className="bg-[#e11d48]/10 text-[#e11d48] p-1.5 rounded-lg">
                              <UserRound size={14} />
                            </span>
                            {complaint.type === "Request"
                              ? "Request to admin"
                              : `Against ${complaint.againstEmployee?.name || complaint.againstEmployeeName || "Employee"}`}
                            {complaint.type !== "Request" && (
                              <span className="opacity-70">({complaint.againstEmployee?.employeeId || complaint.againstEmployeeCode || "-"})</span>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ${statusStyle[complaint.status] || statusStyle.Open}`}>
                            {complaint.status || "Open"}
                          </span>
                        </div>
                        <h3 className="text-base font-black text-[var(--text-primary)]">{complaint.subject || displayType(complaint.type)}</h3>
                        <p className="whitespace-pre-wrap text-sm leading-6 text-[var(--text-secondary)]">
                          {complaint.description}
                        </p>
                        {complaint.adminNote && (
                          <div className="mt-2 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 text-sm flex gap-2 items-start">
                            <span className="font-black text-[#e11d48] mt-0.5"><AlertCircle size={14} /></span>
                            <span className="text-[var(--text-primary)]"><strong className="opacity-80">Admin note:</strong> {complaint.adminNote}</span>
                          </div>
                        )}
                        <div className="mt-2 text-xs font-semibold text-[var(--text-secondary)] flex justify-end">
                          {formatDate(complaint.createdAt)}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {canMonitorComplaints && (
              <section
                id="employee-complaints-list"
                className={`grievance-premium-board !p-6 ${
                  location.search.includes("view=employee-list") ? "ring-2 ring-[#e11d48]" : ""
                }`}
              >
                <div className="grievance-board-header !mb-6">
                  <div>
                    <div className="mb-1 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#e11d48]">
                      <ShieldAlert size={15} />
                      {employee?.role === "lead" ? "Lead Review" : "HR Monitoring"}
                    </div>
                    <h2 className="text-xl font-bold">{employee?.role === "lead" ? "Team" : "Employee"} grievances</h2>
                  </div>
                </div>

                {!teamComplaints.length ? (
                  <div className="rounded-2xl border border-dashed border-[var(--border-color)] p-6 text-center bg-[var(--bg-primary)]/50">
                    <p className="text-sm font-bold text-[var(--text-secondary)]">No team grievances or requests yet.</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {teamComplaints.map((complaint) => (
                      <article key={complaint._id} className="grievance-history-item">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ${statusStyle[complaint.status] || statusStyle.Open}`}>
                              {complaint.status || "Open"}
                            </span>
                            <span className="rounded-full bg-[#e11d48]/10 px-3 py-1 text-xs font-black text-[#e11d48]">
                              {displayType(complaint.type)}
                            </span>
                          </div>
                          <span className="text-xs font-semibold text-[var(--text-secondary)]">{formatDate(complaint.createdAt)}</span>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 mb-4">
                          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                            <p className="text-[10px] font-black uppercase text-[#e11d48] mb-1">Raised by</p>
                            <p className="truncate text-sm font-black">{complaint.raisedBy?.name || complaint.raisedByName || "Employee"}</p>
                            <p className="truncate text-xs font-semibold text-[var(--text-secondary)]">
                              {complaint.raisedBy?.employeeId || complaint.raisedByEmployeeId || "-"}
                            </p>
                          </div>
                          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                            <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] mb-1">Against</p>
                            <p className="truncate text-sm font-black">
                              {complaint.type === "Request" ? "Admin request" : complaint.againstEmployee?.name || complaint.againstEmployeeName || "Employee"}
                            </p>
                            <p className="truncate text-xs font-semibold text-[var(--text-secondary)]">
                              {complaint.type === "Request" ? "No employee ID needed" : complaint.againstEmployee?.employeeId || complaint.againstEmployeeCode || "-"}
                            </p>
                          </div>
                        </div>
                        <h3 className="text-base font-black text-[var(--text-primary)]">{complaint.subject || displayType(complaint.type)}</h3>
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--text-secondary)]">
                          {complaint.description}
                        </p>
                        {complaint.adminNote && (
                          <div className="mt-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 text-sm flex gap-2 items-start">
                            <span className="font-black text-[#e11d48] mt-0.5"><AlertCircle size={14} /></span>
                            <span className="text-[var(--text-primary)]"><strong className="opacity-80">Admin note:</strong> {complaint.adminNote}</span>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeComplaints;
